/**
 * useEnergyRates - Hook for managing energy rate profiles
 * Provides CRUD operations and state management for the Electric Rates settings page
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import type { EnergyRate, RateFormData, RateListResponse, RateAssignmentCounts } from '@/types/rates';
import { authenticatedFetch } from '@/lib/api';

const API_BASE = '/api/v1/admin/rates';

interface UseEnergyRatesOptions {
  autoFetch?: boolean;
  pageSize?: number;
}

interface UseEnergyRatesReturn {
  // Data
  rates: EnergyRate[];
  totalCount: number;
  page: number;
  pageSize: number;

  // Loading states
  isLoading: boolean;
  isSubmitting: boolean;

  // Error state
  error: string | null;

  // Actions
  fetchRates: (options?: { page?: number; search?: string; country?: string; state?: string }) => Promise<void>;
  createRate: (data: RateFormData) => Promise<EnergyRate | null>;
  updateRate: (id: string, data: Partial<RateFormData>) => Promise<EnergyRate | null>;
  deleteRate: (id: string) => Promise<boolean>;
  toggleLock: (id: string, isLocked: boolean) => Promise<EnergyRate | null>;
  getAssignments: (id: string) => Promise<RateAssignmentCounts | null>;
  getAllAssignments: () => Promise<Record<string, RateAssignmentCounts>>;
  setPage: (page: number) => void;
  refresh: () => Promise<void>;
}

export function useEnergyRates(options: UseEnergyRatesOptions = {}): UseEnergyRatesReturn {
  const { autoFetch = true, pageSize: defaultPageSize = 50 } = options;

  const [rates, setRates] = useState<EnergyRate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(defaultPageSize);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<{ search?: string; country?: string; state?: string }>({});

  const fetchRates = useCallback(async (fetchOptions?: { page?: number; search?: string; country?: string; state?: string }) => {
    setIsLoading(true);
    setError(null);

    const targetPage = fetchOptions?.page ?? page;
    const search = fetchOptions?.search ?? currentFilters.search;
    const country = fetchOptions?.country ?? currentFilters.country;
    const state = fetchOptions?.state ?? currentFilters.state;

    // Update current filters
    if (fetchOptions?.search !== undefined || fetchOptions?.country !== undefined || fetchOptions?.state !== undefined) {
      setCurrentFilters({ search, country, state });
    }

    try {
      const params = new URLSearchParams();
      params.set('page', String(targetPage));
      params.set('pageSize', String(pageSize));
      if (search) params.set('search', search);
      if (country) params.set('country', country);
      if (state) params.set('state', state);

      const response = await authenticatedFetch(`${API_BASE}?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data: RateListResponse = await response.json();
      setRates(data.rates);
      setTotalCount(data.total);
      setPage(data.page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch rates';
      setError(message);
      console.error('[useEnergyRates] fetchRates error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, currentFilters]);

  const createRate = useCallback(async (data: RateFormData): Promise<EnergyRate | null> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      toast.success(`Rate profile "${result.rate.name}" created`);

      // Refresh the list
      await fetchRates();

      return result.rate;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create rate';
      setError(message);
      toast.error(message);
      console.error('[useEnergyRates] createRate error:', err);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchRates]);

  const updateRate = useCallback(async (id: string, data: Partial<RateFormData>): Promise<EnergyRate | null> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      toast.success(`Rate profile "${result.rate.name}" updated`);

      // Update local state
      setRates(prev => prev.map(r => r.id === id ? result.rate : r));

      return result.rate;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update rate';
      setError(message);
      toast.error(message);
      console.error('[useEnergyRates] updateRate error:', err);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const deleteRate = useCallback(async (id: string): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      toast.success('Rate profile deleted');

      // Refresh the list
      await fetchRates();

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete rate';
      setError(message);
      toast.error(message);
      console.error('[useEnergyRates] deleteRate error:', err);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchRates]);

  const toggleLock = useCallback(async (id: string, isLocked: boolean): Promise<EnergyRate | null> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/${id}/lock`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isLocked }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      const action = result.rate.isLocked ? 'locked' : 'unlocked';
      toast.success(`Rate profile "${result.rate.name}" ${action}`);

      // Update local state
      setRates(prev => prev.map(r => r.id === id ? result.rate : r));

      return result.rate;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to toggle lock';
      setError(message);
      toast.error(message);
      console.error('[useEnergyRates] toggleLock error:', err);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const getAssignments = useCallback(async (id: string): Promise<RateAssignmentCounts | null> => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/${id}/assignments`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return {
        rateId: data.rateId,
        companyCount: data.assignments.companies,
        groupCount: data.assignments.groups,
        unitCount: data.assignments.units,
        totalCount: data.assignments.total,
      };
    } catch (err) {
      console.error('[useEnergyRates] getAssignments error:', err);
      return null;
    }
  }, []);

  const getAllAssignments = useCallback(async (): Promise<Record<string, RateAssignmentCounts>> => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/all/assignments`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const result: Record<string, RateAssignmentCounts> = {};

      for (const item of data.assignments) {
        result[item.rateId] = {
          rateId: item.rateId,
          companyCount: item.companies,
          groupCount: item.groups,
          unitCount: item.units,
          totalCount: item.total,
        };
      }

      return result;
    } catch (err) {
      console.error('[useEnergyRates] getAllAssignments error:', err);
      return {};
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchRates();
  }, [fetchRates]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchRates();
    }
  }, [autoFetch]); // Only run on mount, not when fetchRates changes

  return {
    rates,
    totalCount,
    page,
    pageSize,
    isLoading,
    isSubmitting,
    error,
    fetchRates,
    createRate,
    updateRate,
    deleteRate,
    toggleLock,
    getAssignments,
    getAllAssignments,
    setPage,
    refresh,
  };
}
